import { useState } from 'react';
import { CardBackLogo } from './CardBackLogos';
import { LOGO_NAMES } from '../constants/cardBack';
import { CARD_BACK_LOGOS, type CardBackLogoType, type CardBackStyle } from '../types';
import './DownloadDialog.css';

interface DownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    includeInstructions: boolean,
    includeCardBacks: boolean,
    logoType: CardBackLogoType,
    style: CardBackStyle
  ) => void;
  previewColor: string;
  isGenerating: boolean;
}

export function DownloadDialog({
  isOpen,
  onClose,
  onConfirm,
  previewColor,
  isGenerating,
}: DownloadDialogProps) {
  const [includeInstructions, setIncludeInstructions] = useState(false);
  const [includeCardBacks, setIncludeCardBacks] = useState(false);
  const [logoType, setLogoType] = useState<CardBackLogoType>('d20');
  const [style, setStyle] = useState<CardBackStyle>('solid');

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(includeInstructions, includeCardBacks, logoType, style);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isGenerating) {
      onClose();
    }
  };

  return (
    <div className="download-dialog-backdrop" onClick={handleBackdropClick}>
      <div className="download-dialog">
        <div className="download-dialog-header">
          <h2>Download Cards</h2>
          {!isGenerating && (
            <button className="close-btn" onClick={onClose} aria-label="Close">
              ×
            </button>
          )}
        </div>

        <div className="download-dialog-content">
          <div className="instructions-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={includeInstructions}
                onChange={(e) => setIncludeInstructions(e.target.checked)}
                disabled={isGenerating}
              />
              <span>Include Instructions</span>
            </label>
            <p className="toggle-description">
              Print settings guide included as page 1
            </p>
          </div>

          <div className="card-backs-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={includeCardBacks}
                onChange={(e) => setIncludeCardBacks(e.target.checked)}
                disabled={isGenerating}
              />
              <span>Include Card Backs</span>
            </label>
            <p className="toggle-description">
              Card backs will be interleaved with fronts for double-sided printing
            </p>
          </div>

          {includeCardBacks && (
            <div className="card-back-options">
              <div className="style-section">
                <h3>Background Style</h3>
                <div className="style-buttons">
                  <button
                    className={`style-btn ${style === 'solid' ? 'active' : ''}`}
                    onClick={() => setStyle('solid')}
                    disabled={isGenerating}
                  >
                    <div 
                      className="style-preview"
                      style={{ backgroundColor: previewColor }}
                    >
                      <CardBackLogo type={logoType} color="#fff" size={28} />
                    </div>
                    <span>Solid Color</span>
                    <p className="style-description">Colored background with white logo</p>
                  </button>
                  <button
                    className={`style-btn ${style === 'white' ? 'active' : ''}`}
                    onClick={() => setStyle('white')}
                    disabled={isGenerating}
                  >
                    <div 
                      className="style-preview white"
                      style={{ borderColor: previewColor }}
                    >
                      <CardBackLogo type={logoType} color={previewColor} size={28} />
                    </div>
                    <span>White</span>
                    <p className="style-description">White background with colored logo</p>
                  </button>
                </div>
              </div>

              <div className="logo-section">
                <h3>Logo Design</h3>
                <div className="logo-grid">
                  {CARD_BACK_LOGOS.map((logo) => {
                    const isSelected = logo === logoType;
                    const bgColor = style === 'solid' ? previewColor : '#fff';
                    const logoColor = style === 'solid' ? '#fff' : previewColor;
                    const textColor = style === 'solid' ? '#fff' : previewColor;
                    
                    return (
                      <button
                        key={logo}
                        className={`logo-btn ${isSelected ? 'selected' : ''}`}
                        onClick={() => setLogoType(logo)}
                        disabled={isGenerating}
                        title={LOGO_NAMES[logo]}
                        style={{
                          backgroundColor: bgColor,
                          borderColor: isSelected ? previewColor : 'transparent',
                        }}
                      >
                        <CardBackLogo type={logo} color={logoColor} size={40} />
                        <span className="logo-name" style={{ color: textColor }}>
                          {LOGO_NAMES[logo]}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="download-dialog-footer">
          <button 
            className="cancel-btn" 
            onClick={onClose}
            disabled={isGenerating}
          >
            Cancel
          </button>
          <button 
            className="confirm-btn" 
            onClick={handleConfirm}
            disabled={isGenerating}
          >
            {isGenerating ? 'Generating PDF...' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
